import { ExclamationIcon } from "@heroicons/react/outline";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { WorkflowContext, useModal } from "../contexts";
import ToggleButton from "./ToggleButton";
import WorkflowAutomationPanelManual from "./WorkflowAutomationPanelManual";
import SmallSelectField from "./SmallSelectField";
import { getData, patchData } from "../backend";
import { PipelineStage, HubspotConfig, Pipeline } from "../interfaces";
import { Link, useRouteMatch } from "react-router-dom";
import useUser from "../hooks/useUser";
import PayWallModalPro from "./PayWallModalPro";
import useSWR from "swr";
import { useTranslation } from "react-i18next";


function WorkflowAutomationPanelHubspot() {
  const { workflow, mutateWorkflow, automationOptions, updateAutoCreate } = useContext(WorkflowContext);
  const { t } = useTranslation();

  const { user } = useUser();
  const { openModal } = useModal();

  const [config, setConfig] = useState<HubspotConfig | undefined>(workflow.source?.hubspotConfig);
  const { data: pipelines } = useSWR<Array<Pipeline>>(workflow.source?.hubspotConfig?.hubspotObjectTypeId === "0-3" ? `/hubspot/pipelines/` : null, getData);

  const { url } = useRouteMatch();

  useEffect(() => {
    setConfig(workflow.source?.hubspotConfig);
  }, [workflow.source?.hubspotConfig]);

  async function handleUpdateAutoCreate(autoCreate: boolean) {
    if (autoCreate && !user?.subscription?.featureHubspotAutocreate)
      return openModal(<PayWallModalPro />);

    updateAutoCreate(autoCreate);
  }

  const updateHubspotSourceConfig = useCallback((updates: any) => {
    setConfig(c => ({ ...c, ...updates }));

    patchData(`/hubspot/config/${workflow.source!.hubspotConfig?.id}/`, updates)
      .then(() => mutateWorkflow())
      .catch(console.error);

  }, [mutateWorkflow, workflow.source])

  // TODO: Do we need set the default pipeline and deal stage when the user selects a new pipeline?

  // useEffect(() => {
  //   if (!config || config.hubspotObjectTypeId !== "0-3" || pipelines) // deals
  //     return;

  //   if (pipelines.length >= 1 && !pipelines.find((p: Pipeline) => p.id === config.notificationPipeline))
  //     updateHubspotSourceConfig({ notificationPipeline: pipelines[0].id, notificationDealstage: "" })

  // }, [config, pipelines, updateHubspotSourceConfig]);

  const pipelineStages = useMemo<Array<PipelineStage>>(() => {
    return pipelines?.find(p => p.id === config?.notificationPipeline)?.stages ?? [];
  }, [pipelines, config?.notificationPipeline]);

  return (
    <>
      {/* Primary Actions - Run Workflow */}
      <div className="flex flex-col gap-2 select-none">
        <div className="group flex items-center gap-1 font-gilroy font-semibold text-sm">
          {t("Run Workflow")}
        </div>

        {(automationOptions.selectedTotal === 0 || !automationOptions.selectedTotal) &&
          <Link to={url + "/source"} className="flex items-center text-sm w-full text-yellow font-semibold hover:underline">
            <ExclamationIcon className="w-4 h-4 mr-2" /> {t("Select HubSpot data from table")}
          </Link>
        }
        {automationOptions.selectedTotal === 1 &&
          <div className="text-sm w-full text-gray-600 font-semibold">
            {t("Process")} <b>1</b> {t("selected item")}
          </div>
        }
        {automationOptions.selectedTotal > 1 &&
          <div className="text-sm w-full text-gray-600 font-semibold">
            {t("Process")} <b>{automationOptions.selectedTotal}</b> {t("selected items")}
          </div>
        }
        <WorkflowAutomationPanelManual disabled={automationOptions.selectedTotal === 0 || !automationOptions.selectedTotal} />
      </div>

      <div className="flex items-center gap-2 my-2">
        <div className="h-px bg-gray-400 w-full" />
        <div className="flex-shrink-0 font-gilroy font-semibold text-xs text-gray-600">{t("or")}</div>
        <div className="h-px bg-gray-400 w-full" />
      </div>

      {/* Auto-Create Section */}
      <div className="flex flex-col gap-2">
        <div className="flex justify-between font-gilroy font-semibold text-sm">
          {t("Auto-Create")}
          <ToggleButton value={workflow.autoCreate} onChange={handleUpdateAutoCreate} bgColor="bg-green" disabled={config?.hubspotObjectTypeId !== "0-3"} />
        </div>

        <div className="text-xs text-gray-600">
          {t("Run this workflow when deals are moved into the selected deal stage (only available for deals).")}

          {workflow.autoCreate && config &&
            <div className="flex flex-col gap-1 mt-1">
              {((pipelines?.length ?? 0) > 1) &&
                <SmallSelectField value={config.notificationPipeline} onChange={(notificationPipeline) => updateHubspotSourceConfig({ notificationPipeline, notificationDealstage: "" })} label={t("Pipeline")}>
                  {pipelines!.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </SmallSelectField>
              }
              <SmallSelectField value={!!pipelines ? config.notificationDealstage : ""} onChange={(notificationDealstage) => updateHubspotSourceConfig({ notificationPipeline: config.notificationPipeline, notificationDealstage })} label={t("Deal Stage")} invalid={config?.notificationDealstage === ""} disabled={pipelines === undefined}>
                <>
                  <option value="" disabled>{pipelines === undefined ? t("Loading Pipelines...") : t("Please select deal stage")}</option>
                  {pipelineStages.map(ds => <option key={ds.id} value={ds.id}>{ds.label}</option>)}
                </>
              </SmallSelectField>
            </div>
          }
        </div>
      </div>
    </>
  );

}

export default WorkflowAutomationPanelHubspot;
